import { getServerUrl, publicAnonKey, supabase } from './woocommerce';
import { getSwiftWooJwt, isLikelySwiftWooJwt } from './jwt';

export interface TenantBrand {
  brand_key: string;
  domain: string;
  store_url: string;
  app_url: string;
  brand_name: string;
  logo_url: string;
  primary_color: string;
  active: boolean;
  extra: any;
}

export interface BootstrapAuthInfo {
  token_endpoint: string;
  refresh_endpoint: string;
  validate_endpoint: string;
  me_endpoint: string;
}

export interface WebhooksConfig {
  base_url: string;
  enabled: boolean;
  endpoints: {
    order_created: string;
    order_updated: string;
    product_updated: string;
    customer_updated: string;
  };
}

export interface BootstrapPayload {
  brand: TenantBrand;
  auth: BootstrapAuthInfo;
  webhooks: WebhooksConfig;
}

export class PluginApiClient {
  private static async proxyJson(
    storeUrl: string,
    endpoint: string,
    method: string,
    headers: Record<string, string> = {},
    body?: BodyInit,
  ): Promise<any> {
    const baseUrl = (storeUrl || '').replace(/\/+$/, '').trim();
    if (!baseUrl) {
      throw new Error('Store URL missing.');
    }

    const target = new URL(endpoint.startsWith('/') ? endpoint.slice(1) : endpoint, `${baseUrl}/`).toString();

    const requestHeaders = new Headers(headers);
    requestHeaders.set('X-Proxy-Target', target);
    requestHeaders.set('apikey', publicAnonKey);
    const appDomain = localStorage.getItem('wc_app_domain') || window.location.hostname;
    requestHeaders.set('X-SwiftWoo-Domain', appDomain);
    if (!requestHeaders.has('Accept')) requestHeaders.set('Accept', 'application/json');

    const response = await fetch(getServerUrl('proxy'), {
      method,
      headers: requestHeaders,
      body,
    });

    const text = await response.text();
    let data: any = null;
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = text;
      }
    }

    if (!response.ok) {
      const message = data?.message || data?.error || `Request failed (${response.status})`;
      throw new Error(message);
    }

    return data;
  }

  static async login(
    storeUrl: string,
    usernameOrEmail: string,
    password: string,
    remember = false,
  ): Promise<{ token: string; expires_at: number; user: any; brand?: TenantBrand }> {
    const data = await this.proxyJson(
      storeUrl,
      '/wp-json/swiftwoo/v1/token',
      'POST',
      {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      JSON.stringify({ username: usernameOrEmail, password, remember }),
    );

    if (!data?.token) {
      throw new Error(data?.message || 'Login falhou.');
    }

    return data;
  }

  static async validate(storeUrl: string, token: string): Promise<boolean> {
    try {
      const data = await this.proxyJson(
        storeUrl,
        '/wp-json/swiftwoo/v1/token/validate',
        'GET',
        {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-WC-Authorization': `Bearer ${token}`,
        },
      );

      return data !== false && data !== null && data !== undefined && data?.valid !== false;
    } catch {
      return false;
    }
  }

  static async refresh(storeUrl: string, token: string): Promise<{ token: string; expires_at: number; brand?: TenantBrand }> {
    const data = await this.proxyJson(
      storeUrl,
      '/wp-json/swiftwoo/v1/token/refresh',
      'POST',
      {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    );

    if (!data?.token) {
      throw new Error(data?.message || 'Refresh failed.');
    }

    return data;
  }

  static async revoke(storeUrl: string, token: string): Promise<void> {
    try {
      await this.proxyJson(
        storeUrl,
        '/wp-json/swiftwoo/v1/token/revoke',
        'POST',
        {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      );
    } catch {
      // Best-effort
    }
  }

  static async getMe(token: string): Promise<any> {
    const authEndpoints = JSON.parse(localStorage.getItem('swiftwoo_auth_endpoints') || '{}');
    const storeUrl = localStorage.getItem('wc_active_store_url') || '';
    const meEndpoint = authEndpoints.me_endpoint || `${(storeUrl || '').replace(/\/+$/, '')}/wp-json/swiftwoo/v1/me`;
    const appDomain = localStorage.getItem('wc_app_domain') || window.location.hostname;

    const response = await fetch(getServerUrl('proxy'), {
      method: 'GET',
      headers: {
        'X-Proxy-Target': meEndpoint,
        'Accept': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-WC-Authorization': `Bearer ${token}`,
        'X-SwiftWoo-Domain': appDomain,
        apikey: publicAnonKey,
      },
    });

    const text = await response.text();
    let data: any = null;
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = text;
      }
    }

    if (!response.ok) {
      throw new Error(data?.message || 'Falha ao obter perfil.');
    }

    return data;
  }

  static async getWebhooksConfig(): Promise<WebhooksConfig> {
    const storeUrl = localStorage.getItem('wc_active_store_url') || '';
    const url = `${(storeUrl || '').replace(/\/+$/, '')}/wp-json/swiftwoo/v1/webhooks/config`;
    const appDomain = localStorage.getItem('wc_app_domain') || window.location.hostname;

    const response = await fetch(getServerUrl('proxy'), {
      method: 'GET',
      headers: {
        'X-Proxy-Target': url,
        'Accept': 'application/json',
        'X-SwiftWoo-Domain': appDomain,
        apikey: publicAnonKey,
      },
    });

    const text = await response.text();
    let data: any = null;
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = text;
      }
    }

    if (!response.ok) {
      throw new Error(data?.message || 'Falha ao carregar configuração de webhooks.');
    }

    return data;
  }

  static async clientRequest(storeUrl: string, endpoint: string, options: RequestInit = {}): Promise<Response> {
    const activeId = localStorage.getItem('wc_active_store_id') || 'default';
    let token = localStorage.getItem(`client_token_${activeId}`);
    const baseUrl = (storeUrl || '').replace(/\/+$/, '');
    const url = `${baseUrl}/${endpoint.replace(/^\/+/, '')}`;
    const appDomain = localStorage.getItem('wc_app_domain') || window.location.hostname;

    const headers = new Headers(options.headers || {});
    if (token && !headers.has('X-WC-Authorization')) {
      headers.set('X-WC-Authorization', `Bearer ${token}`);
    }

    let response = await fetch(getServerUrl('proxy'), {
      ...options,
      headers: {
        ...Object.fromEntries(headers.entries()),
        'X-Proxy-Target': url,
        'X-SwiftWoo-Domain': appDomain,
        apikey: publicAnonKey,
      },
    });

    if (response.status === 401 && token) {
      try {
        const refreshData = await this.refresh(storeUrl, token);
        if (refreshData && refreshData.token) {
          token = refreshData.token;
          localStorage.setItem(`client_token_${activeId}`, token);
          headers.set('X-WC-Authorization', `Bearer ${token}`);
          response = await fetch(getServerUrl('proxy'), {
            ...options,
            headers: {
              ...Object.fromEntries(headers.entries()),
              'X-Proxy-Target': url,
              'X-SwiftWoo-Domain': appDomain,
              apikey: publicAnonKey,
            },
          });
        }
      } catch (e) {
        console.warn('Failed to refresh client token:', e);
      }
    }

    return response;
  }

  static async staffRequest(storeUrl: string, endpoint: string, options: RequestInit = {}): Promise<Response> {
    const { data, error } = await supabase.auth.getSession();

    if (error || !data.session?.access_token) {
      throw new Error('Active session expired or invalid. Please re-authenticate.');
    }

    const token = data.session.access_token;
    const baseUrl = (storeUrl || '').replace(/\/+$/, '');
    const url = `${baseUrl}/${endpoint.replace(/^\/+/, '')}`;
    const appDomain = localStorage.getItem('wc_app_domain') || window.location.hostname;

    const headers = new Headers(options.headers || {});
    headers.set('Authorization', `Bearer ${token}`);
    headers.set('X-WC-Authorization', `Bearer ${token}`);
    
    if (!headers.has('Accept')) {
      headers.set('Accept', 'application/json');
    }

    const response = await fetch(getServerUrl('proxy'), {
      ...options,
      headers: {
        ...Object.fromEntries(headers.entries()),
        'X-Proxy-Target': url,
        'X-SwiftWoo-Domain': appDomain,
        apikey: publicAnonKey,
      },
    });

    return response;
  }
}
